import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip, Slider,
  useTheme, Alert, TextField,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RouteIcon from '@mui/icons-material/Route';
import DownloadIcon from '@mui/icons-material/Download';
import MapView from '../MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { readFileAsText, parseAnyGeoFile, haversineDistance, calculateBearing, exportGeoJSON } from '../../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';

/**
 * Simple client-side route snapping using nearest-neighbor projection onto a road segment grid.
 * This is a simplified algorithm that:
 * 1. Smooths the GPS trace using a moving average
 * 2. Removes outlier points based on speed/angle
 * 3. Interpolates missing segments
 * 
 * For production use, connect to OSRM/Google Roads/Mapbox Matching API.
 */
function snapToSmoothed(coords, windowSize = 3) {
  if (coords.length < windowSize) return [...coords];
  const smoothed = [];
  for (let i = 0; i < coords.length; i++) {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(coords.length, i + Math.floor(windowSize / 2) + 1);
    const window = coords.slice(start, end);
    const avgLon = window.reduce((s, c) => s + c[0], 0) / window.length;
    const avgLat = window.reduce((s, c) => s + c[1], 0) / window.length;
    smoothed.push([avgLon, avgLat]);
  }
  return smoothed;
}

function removeOutliersByAngle(coords, maxAngleChange = 120) {
  if (coords.length < 3) return [...coords];
  const result = [coords[0], coords[1]];
  for (let i = 2; i < coords.length; i++) {
    const b1 = calculateBearing(coords[i - 2], coords[i - 1]);
    const b2 = calculateBearing(coords[i - 1], coords[i]);
    let angleDiff = Math.abs(b2 - b1);
    if (angleDiff > 180) angleDiff = 360 - angleDiff;
    if (angleDiff < maxAngleChange) {
      result.push(coords[i]);
    }
  }
  return result;
}

function interpolateGaps(coords, maxGapM = 100) {
  if (coords.length < 2) return [...coords];
  const result = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    const dist = haversineDistance(coords[i - 1], coords[i]);
    if (dist > maxGapM) {
      const segments = Math.ceil(dist / maxGapM);
      for (let j = 1; j <= segments; j++) {
        const t = j / segments;
        result.push([
          coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
          coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
        ]);
      }
    } else {
      result.push(coords[i]);
    }
  }
  return result;
}

function RouteSnappingPlayground() {
  const theme = useTheme();
  const [rawCoords, setRawCoords] = useState([]);
  const [error, setError] = useState('');
  const [smoothWindow, setSmoothWindow] = useState(5);
  const [maxAngle, setMaxAngle] = useState(100);
  const [maxGap, setMaxGap] = useState(200);
  const [osrmUrl, setOsrmUrl] = useState('');
  const [osrmResult, setOsrmResult] = useState(null);
  const [osrmLoading, setOsrmLoading] = useState(false);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse');
      const fc = result.geoJson;
      let coords = [];
      for (const f of fc.features) {
        if (f.geometry?.type === 'LineString') coords = f.geometry.coordinates;
        else if (f.geometry?.type === 'Point') coords.push(f.geometry.coordinates);
      }
      if (coords.length < 2) throw new Error('Need at least 2 coordinates');
      setRawCoords(coords);
      setOsrmResult(null);
      setError('');
    } catch (err) { setError(err.message); }
  }, []);

  // Client-side snapping
  const snappedCoords = useMemo(() => {
    if (rawCoords.length < 3) return rawCoords;
    let result = [...rawCoords];
    result = removeOutliersByAngle(result, maxAngle);
    result = snapToSmoothed(result, smoothWindow);
    result = interpolateGaps(result, maxGap);
    return result;
  }, [rawCoords, smoothWindow, maxAngle, maxGap]);

  // OSRM match (optional)
  const handleOsrmMatch = useCallback(async () => {
    if (!osrmUrl || rawCoords.length < 2) return;
    setOsrmLoading(true);
    try {
      const coordStr = rawCoords.map(c => `${c[0]},${c[1]}`).join(';');
      const url = `${osrmUrl}/match/v1/driving/${coordStr}?overview=full&geometries=geojson`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (data.code !== 'Ok' || !data.matchings?.[0]) throw new Error(data.message || 'Match failed');
      setOsrmResult(data.matchings[0].geometry.coordinates);
      setError('');
    } catch (err) {
      setError(`OSRM error: ${err.message}`);
    } finally {
      setOsrmLoading(false);
    }
  }, [osrmUrl, rawCoords]);

  const displaySnapped = osrmResult || snappedCoords;

  const mapGeoJson = useMemo(() => {
    if (rawCoords.length === 0) return null;
    const features = [];
    // Raw route (red dashed)
    features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: rawCoords }, properties: { layer: 'raw' } });
    // Snapped route (blue solid)
    if (displaySnapped.length > 1) {
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: displaySnapped }, properties: { layer: 'snapped' } });
    }
    return { type: 'FeatureCollection', features };
  }, [rawCoords, displaySnapped]);

  const styleFunction = useCallback((feature) => {
    if (feature.properties.layer === 'raw') return { color: '#ef4444', weight: 2, opacity: 0.6, dashArray: '6 4' };
    if (feature.properties.layer === 'snapped') return { color: '#3b82f6', weight: 3.5, opacity: 0.9 };
    return {};
  }, []);

  const stats = useMemo(() => {
    if (rawCoords.length < 2) return null;
    let rawDist = 0;
    for (let i = 1; i < rawCoords.length; i++) rawDist += haversineDistance(rawCoords[i - 1], rawCoords[i]);
    let snappedDist = 0;
    for (let i = 1; i < displaySnapped.length; i++) snappedDist += haversineDistance(displaySnapped[i - 1], displaySnapped[i]);
    return {
      rawPoints: rawCoords.length,
      snappedPoints: displaySnapped.length,
      rawDistance: rawDist,
      snappedDistance: snappedDist,
      reduction: ((1 - snappedDist / rawDist) * 100),
      source: osrmResult ? 'OSRM' : 'Client-side',
    };
  }, [rawCoords, displaySnapped, osrmResult]);

  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(0)} m`;

  const handleExport = () => {
    const fc = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: displaySnapped }, properties: { name: 'Snapped Route', source: stats?.source } }],
    };
    exportGeoJSON(fc, 'geojson', 'snapped_route');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Route Snapping Playground | OSRM Map Matching | UmbrellaCo"
        description="Snap raw GPS traces to road networks using client-side algorithms or OSRM map matching. Test smoothing, interpolation, and snapping parameters online."
        keywords="route snapping, map matching, osrm matching, snap to road, gps smoothing"
        url="/spatial-tools/route-snapping"
        jsonLd={createSoftwareAppSchema(
          'Route Snapping Playground',
          'Experiment with GPS trace smoothing and map matching using OSRM.',
          'https://umbrellaco.in/spatial-tools/route-snapping',
          ['Map Matching', 'Route Snapping', 'GPS Smoothing']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Route Snapping Playground</Typography>
        <Typography variant="body2" color="text.secondary">
          Experiment with client-side GPS smoothing and interpolation algorithms, or test true map-matching using an OSRM backend to snap traces to the road network.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={32} minSize={22}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ p: 2 }}>
              <Button component="label" variant="contained" fullWidth size="small" startIcon={<UploadFileIcon />}
                sx={{ background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}>
                {rawCoords.length > 0 ? `Loaded (${rawCoords.length} pts)` : 'Upload GPS Trace'}
                <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleUpload} />
              </Button>
              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                <Chip size="small" sx={{ bgcolor: '#ef444422', color: '#ef4444', fontWeight: 600 }} label="━ Raw" />
                <Chip size="small" sx={{ bgcolor: '#3b82f622', color: '#3b82f6', fontWeight: 600 }} label="━ Snapped" />
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                <RouteIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Client-Side Snapping</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Smoothing Window</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Slider value={smoothWindow} onChange={(e, v) => setSmoothWindow(v)} min={1} max={15} step={2} size="small" sx={{ flex: 1 }} />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 25 }}>{smoothWindow}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Max Angle Change (°)</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Slider value={maxAngle} onChange={(e, v) => setMaxAngle(v)} min={30} max={180} step={5} size="small" sx={{ flex: 1 }} />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 30 }}>{maxAngle}°</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Interpolation Gap (m)</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Slider value={maxGap} onChange={(e, v) => setMaxGap(v)} min={20} max={1000} step={20} size="small" sx={{ flex: 1 }} />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{maxGap}m</Typography>
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>OSRM Matching (optional)</Typography>
              <TextField fullWidth size="small" value={osrmUrl} onChange={(e) => setOsrmUrl(e.target.value)}
                placeholder="http://router.project-osrm.org"
                sx={{ mb: 1, '& input': { fontSize: '0.8rem' } }}
              />
              <Button variant="outlined" fullWidth size="small" onClick={handleOsrmMatch}
                disabled={!osrmUrl || rawCoords.length < 2 || osrmLoading}>
                {osrmLoading ? 'Matching...' : 'Match via OSRM'}
              </Button>
            </Box>
            <Divider />
            {stats && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Stats</Typography>
                <Chip size="small" label={`Source: ${stats.source}`} sx={{ mb: 1, fontWeight: 600 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {[
                    { label: 'Raw Points', value: stats.rawPoints },
                    { label: 'Snapped Points', value: stats.snappedPoints },
                    { label: 'Raw Distance', value: fmtDist(stats.rawDistance) },
                    { label: 'Snapped Distance', value: fmtDist(stats.snappedDistance) },
                  ].map(({ label, value }) => (
                    <Paper key={label} variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
            {displaySnapped.length > 1 && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, mt: 'auto', flexShrink: 0 }}>
                <Button variant="outlined" fullWidth size="small" startIcon={<DownloadIcon />} onClick={handleExport}>
                  Export Snapped Route
                </Button>
              </Box>
            )}
          </Box>
        </Panel>
        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />
        <Panel defaultSize={68} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {mapGeoJson ? (
              <MapView mapId="route-snap-map" geoJsonData={mapGeoJson} styleFunction={styleFunction} />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                <Stack alignItems="center" spacing={2} sx={{ p: 4, textAlign: 'center' }}>
                  <RouteIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload a GPS trace to snap</Typography>
                  <Typography variant="body2" color="text.disabled">Client-side smoothing + optional OSRM matching</Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default RouteSnappingPlayground;
